import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  Globe,
  Star,
  Loader2,
  Pencil,
  X,
  Plus,
} from 'lucide-react';
import { PhotoTagEntityType, EntityType, isPlannerOrAbove } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import {
  useVendor,
  useVendorCategories,
  useAddVendorCategory,
  useRemoveVendorCategory,
} from '../hooks/useVendors';
import { ExpenseListView } from '../components/expenses/ExpenseListView';
import { ContactListView } from '../components/contacts';
import { PhotoListView } from '../components/photos/PhotoListView';
import { EntityModal, PageSectionsLayout } from '../components/common';
import type { SectionDef } from '../components/common';
import { RecordConversations } from '../components/messaging/RecordConversations';
import { VendorDetailPanel, VendorBalanceSummary } from '../components/vendors';
import { CommissionListView } from '../components/commissions/CommissionListView';

export function VendorDetailPage() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [showEdit, setShowEdit] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const { data: vendor, isLoading } = useVendor(vendorId || '');
  const { data: allCategories = [] } = useVendorCategories();
  const addCategory = useAddVendorCategory(vendorId || '');
  const removeCategory = useRemoveVendorCategory(vendorId || '');

  const canManageCategories = currentUser ? isPlannerOrAbove(currentUser.systemRole) : false;

  const assignedIds = new Set(vendor?.categories.map((c) => c.vendorCategoryId) ?? []);
  const availableCategories = allCategories.filter((c) => c.isActive && !assignedIds.has(c.id));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Vendor not found</p>
        <button
          onClick={() => navigate('/vendors')}
          className="mt-4 text-primary-600 hover:text-primary-700 text-sm"
        >
          Back to Vendors
        </button>
      </div>
    );
  }

  const sections: SectionDef[] = [
    {
      id: 'conversations',
      title: 'Conversations',
      content: <RecordConversations entityType="Vendor" entityId={vendorId!} />,
    },
    {
      id: 'contacts',
      title: 'Contacts',
      content: <ContactListView entityType={EntityType.VENDOR} entityId={vendorId!} />,
    },
    {
      id: 'commissions',
      title: 'Commissions',
      content: <CommissionListView vendorId={vendorId} />,
    },
    {
      id: 'expenses',
      title: 'Expenses',
      content: (
        <ExpenseListView
          entityType="VENDOR"
          entityId={vendorId}
          showEventColumn
          currentUser={currentUser ?? undefined}
        />
      ),
    },
    {
      id: 'photos',
      title: 'Photos',
      content: (
        <PhotoListView
          entityType={PhotoTagEntityType.VENDOR}
          entityId={vendorId}
          limit={12}
          enableUpload
          currentUser={currentUser || undefined}
        />
      ),
    },
    ...(vendor.notes
      ? [
          {
            id: 'notes',
            title: 'Notes',
            content: <p className="text-sm text-gray-600 whitespace-pre-wrap">{vendor.notes}</p>,
          },
        ]
      : []),
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 size={24} className="text-primary-600" />
              {vendor.businessName}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
              {vendor.contactName && (
                <span className="text-gray-700 font-medium">{vendor.contactName}</span>
              )}
              {vendor.city && <span>{vendor.city}</span>}
              {vendor.rating != null && (
                <span className="flex items-center gap-0.5 text-amber-500">
                  <Star size={14} className="fill-amber-500" />
                  {vendor.rating}
                </span>
              )}
              {!vendor.isActive && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                  Inactive
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Pencil size={16} />
              Edit
            </button>
            {vendor.phone && (
              <a
                href={`tel:${vendor.phone}`}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                <Phone size={16} />
                {vendor.phone}
              </a>
            )}
            {vendor.email && (
              <a
                href={`mailto:${vendor.email}`}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                <Mail size={16} />
                Email
              </a>
            )}
            {vendor.website && (
              <a
                href={
                  vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                <Globe size={16} />
                Website
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="flex gap-2 flex-wrap items-center mb-6">
        {vendor.categories.map((c) => (
          <span
            key={c.id}
            className="flex items-center gap-1 pl-3 pr-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
          >
            {c.categoryName}
            {c.isPrimary && (
              <span className="ml-1 px-1.5 py-0.5 bg-primary-200 text-primary-800 rounded-full text-[10px] leading-none">
                Primary
              </span>
            )}
            {canManageCategories && (
              <button
                onClick={() => removeCategory.mutate(c.vendorCategoryId)}
                className="ml-0.5 text-primary-500 hover:text-primary-800 leading-none"
                aria-label={`Remove ${c.categoryName}`}
              >
                <X size={12} />
              </button>
            )}
          </span>
        ))}

        {canManageCategories && availableCategories.length > 0 && (
          <div className="relative">
            {showAddCategory ? (
              <select
                autoFocus
                className="text-xs px-2 py-1 border border-primary-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    addCategory.mutate({ vendorCategoryId: e.target.value });
                    setShowAddCategory(false);
                  }
                }}
                onBlur={() => setShowAddCategory(false)}
              >
                <option value="" disabled>
                  Select category…
                </option>
                {availableCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : (
              <button
                onClick={() => setShowAddCategory(true)}
                className="flex items-center gap-1 px-3 py-1 border border-dashed border-primary-400 text-primary-600 rounded-full text-xs hover:bg-primary-50 transition-colors"
              >
                <Plus size={12} />
                Add category
              </button>
            )}
          </div>
        )}
      </div>

      {/* Balance summary */}
      <VendorBalanceSummary vendorId={vendorId!} />

      {/* Sections */}
      <PageSectionsLayout
        pageKey="vendor-detail"
        userId={currentUser?.id ?? 'guest'}
        sections={sections}
      />

      {/* Edit Modal */}
      {currentUser && (
        <EntityModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          title={`Edit: ${vendor.businessName}`}
          size="lg"
        >
          <VendorDetailPanel
            vendorId={vendorId!}
            onClose={() => setShowEdit(false)}
            onUnsavedChange={() => {}}
            currentUser={currentUser}
          />
        </EntityModal>
      )}
    </div>
  );
}
